import React from 'react';
import { formatLocalizedDateTime } from '@saviynt/common';
import {
  Box,
  DateAndTimePicker,
  Icon,
  InlineMessage,
  InputField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { msgs } from '../../../utilities/messages';
import { MINUTE_INTERVAL, START_MODE } from '../constants';
import {
  criticalMessage,
  getConfirmCriticalText,
  getPassphraseCriticalText,
} from '../helpers';

/**
 * Presentational body of the DownloadKeysModal: start-time selection, the
 * active/expiry summary, and the passphrase fields. All state and handlers come
 * from the useDownloadKeyForm hook via the `form` prop.
 */
function DownloadKeysModalContent({ form, localize }) {
  const {
    keyValidityHours,
    isPassphraseMandatory,
    minPassphraseLength,
    maxPassphraseLength,
    passphrase,
    confirmPassphrase,
    showPassphrase,
    startMode,
    scheduledDate,
    scheduledTime,
    isSubmitted,
    maxScheduleDate,
    activeFrom,
    expiresAt,
    passphrasesMismatch,
    passphraseLengthInvalid,
    isPassphraseCritical,
    isConfirmCritical,
    isDateCritical,
    isTimeCritical,
    filterTime,
    handleScheduledDateChange,
    handleTimeChange,
    handleStartModeChange,
    handleTogglePassphrase,
    handleSetPassphrase,
    handleSetConfirmPassphrase,
  } = form;

  // Fresh date on each render so the calendar always disables past dates
  const minDate = new Date();

  const dateCriticalText = isDateCritical
    ? criticalMessage(localize(msgs.pam.apiKeys.download.startDateRequired))
    : null;
  const timeCriticalText = isTimeCritical
    ? criticalMessage(localize(msgs.pam.apiKeys.download.startTimeRequired))
    : null;

  return (
    <Box
      className='DownloadKeysModal-body'
      role='form'
      aria-label={localize(msgs.pam.apiKeys.download.title)}>
      {/*
        Decoy username/password inputs. Browsers autofill saved credentials into
        the nearest matching fields when a password input appears; without these,
        the autofill lands in the landing-page search bar, which triggers a search
        that unmounts this modal. Keeping real decoys here (out of the tab order
        and accessibility tree) absorbs the autofill instead.
      */}
      {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
      <input
        type='text'
        name='username'
        autoComplete='username'
        aria-hidden='true'
        tabIndex={-1}
        className='DownloadKeysModal-autofillDecoy'
      />
      {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
      <input
        type='password'
        name='password'
        autoComplete='new-password'
        aria-hidden='true'
        tabIndex={-1}
        className='DownloadKeysModal-autofillDecoy'
      />
      {/* Start time */}
      <Box className='DownloadKeysModal-field'>
        <Box className='DownloadKeysModal-fieldLabel'>
          <Icon kind='Time' size='smallMedium' />
          <Typography kind='h4'>
            {localize(msgs.pam.apiKeys.download.startTime)}
          </Typography>
        </Box>

        <ToggleButtonGroup
          selection={startMode === START_MODE.NOW ? 1 : 2}
          setSelection={handleStartModeChange}
          aria-label={localize(msgs.pam.apiKeys.download.startTime)}>
          <ToggleButton label={localize(msgs.pam.apiKeys.download.startNow)} />
          <ToggleButton label={localize(msgs.pam.apiKeys.download.schedule)} />
        </ToggleButtonGroup>

        {startMode === START_MODE.SCHEDULE && (
          <Box className='DownloadKeysModal-scheduleInput'>
            <InlineMessage
              text={localize(msgs.pam.apiKeys.download.scheduleInfo)}
              colorTheme='info'
              size='small'
              leftIcon={<Icon kind='AlertInfoFilled' />}
            />
            <Box className='DownloadKeysModal-dateTimeRow'>
              <DateAndTimePicker
                kind='dateStart'
                label={localize(msgs.pam.modalPage.dateAndTimePicker.startDate)}
                startDate={scheduledDate}
                setStartDate={handleScheduledDateChange}
                minDate={minDate}
                maxDate={maxScheduleDate}
                backgroundColor='secondary'
                isCritical={isDateCritical}
                CriticalHelperText={dateCriticalText}
              />
              <DateAndTimePicker
                kind='timeStart'
                timeMin={{ minutes: 0, hours: 0 }}
                timeMax={{ minutes: 45, hours: 23 }}
                label={localize(msgs.pam.modalPage.dateAndTimePicker.startTime)}
                startDate={scheduledTime}
                setStartDate={handleTimeChange}
                minDate={minDate}
                maxDate={maxScheduleDate}
                timeIntervals={MINUTE_INTERVAL}
                dateFormat='h:mm aa'
                timeHeaderText=''
                filterTime={scheduledDate ? filterTime : null}
                backgroundColor='secondary'
                isCritical={isTimeCritical}
                CriticalHelperText={timeCriticalText}
              />
            </Box>
          </Box>
        )}

        <Box
          className='DownloadKeysModal-timeSummary'
          role='status'
          aria-live='polite'
          aria-atomic='true'>
          <Box className='DownloadKeysModal-timeSummaryRow'>
            <Typography kind='body2' color='--color-foreground-neutral-medium'>
              {localize(msgs.pam.apiKeys.download.activeFrom)}
            </Typography>
            <Typography kind='body2-bold'>
              {formatLocalizedDateTime(activeFrom.toISOString(), true, false)}
            </Typography>
          </Box>
          <Box
            className='DownloadKeysModal-timeSummaryDivider'
            aria-hidden='true'
          />
          <Box className='DownloadKeysModal-timeSummaryRow'>
            <Box className='DownloadKeysModal-expiryLabel'>
              <Icon kind='Time' size='small' />
              <Typography
                kind='body2'
                color='--color-foreground-neutral-medium'>
                {localize(msgs.pam.apiKeys.download.expiresLabel)}
              </Typography>
            </Box>
            <Typography kind='body2-bold'>
              {formatLocalizedDateTime(expiresAt.toISOString(), true, false)}
            </Typography>
          </Box>
          <Typography
            kind='body3'
            color='--color-foreground-neutral-subtle'
            className='DownloadKeysModal-timeSummaryNote'>
            {localize(msgs.pam.apiKeys.download.validFor, {
              hours: keyValidityHours,
            })}
          </Typography>
        </Box>
      </Box>

      {/* Passphrase */}
      <Box className='DownloadKeysModal-field'>
        <Typography kind='body3' color='--color-foreground-neutral-medium'>
          {localize(msgs.pam.apiKeys.download.passphraseHint)}
        </Typography>
        <InputField
          name='passphrase'
          label={
            <Typography kind='h4' htmlFor='passphrase'>
              {localize(msgs.pam.apiKeys.download.passphrase)}
            </Typography>
          }
          type={showPassphrase ? 'text' : 'password'}
          placeholder={localize(
            msgs.pam.apiKeys.download.passphrasePlaceholder
          )}
          backgroundColor='secondary'
          value={passphrase}
          setValue={handleSetPassphrase}
          isRequired={isPassphraseMandatory}
          isCritical={isPassphraseCritical}
          CriticalHelperText={getPassphraseCriticalText(
            localize,
            isSubmitted,
            isPassphraseMandatory,
            passphrase,
            passphraseLengthInvalid,
            minPassphraseLength,
            maxPassphraseLength
          )}
          suffixButtonType='password'
          suffixIsToggle={showPassphrase}
          suffixOnClick={handleTogglePassphrase}
          autoComplete='new-password'
        />
      </Box>

      {/* Confirm passphrase */}
      {passphrase.length > 0 && (
        <Box className='DownloadKeysModal-field'>
          <InputField
            name='confirmPassphrase'
            label={
              <Typography kind='h4' htmlFor='confirmPassphrase'>
                {localize(msgs.pam.apiKeys.download.confirmPassphrase)}
              </Typography>
            }
            type={showPassphrase ? 'text' : 'password'}
            placeholder={localize(msgs.pam.apiKeys.download.confirmPlaceholder)}
            backgroundColor='secondary'
            value={confirmPassphrase}
            setValue={handleSetConfirmPassphrase}
            isRequired={isPassphraseMandatory}
            isCritical={isConfirmCritical}
            CriticalHelperText={getConfirmCriticalText(
              localize,
              isSubmitted,
              confirmPassphrase,
              passphrasesMismatch
            )}
            autoComplete='new-password'
          />
        </Box>
      )}

      <InlineMessage
        text={localize(msgs.pam.apiKeys.download.passphraseWarning)}
        colorTheme='info'
        size='medium'
        leftIcon={<Icon kind='AlertInfoFilled' />}
      />
    </Box>
  );
}

DownloadKeysModalContent.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  form: PropTypes.object.isRequired,
  localize: PropTypes.func.isRequired,
};

export default DownloadKeysModalContent;
